import { useRef } from "react";
import dayjs from "dayjs";
import ReactECharts from "echarts-for-react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { darkBg, gridColor, labelColor, tooltipStyle } from "../../api/echartsTheme";
import GraphDownloadButton from "../../../Global/other/GraphDownloadButton";
import { formatSAST } from "../../../Global/other/formatSAST";

const colorValue = "#2E93fA";

export default function RawDataGraph({
    deviceRawData,
    deviceInfo,
    isSuccessDeviceRawData,
    isErrorDeviceRawData,
    errorMessageDeviceRawData,
    isLoadingDeviceRawData,
    startDate,
    endDate,
}) {
    const chartRef = useRef(null);
    if (isLoadingDeviceRawData) {
        return (
            <Card variant="outlined" sx={{ mt: 2, backgroundColor: darkBg, p: 3 }}>
                <CircularProgress size={20} sx={{ color: colorValue }} />
            </Card>
        );
    }

    if (isErrorDeviceRawData) {
        return (
            <Card variant="outlined" sx={{ mt: 2, backgroundColor: darkBg, p: 3 }}>
                <Typography sx={{ color: "#EF4444" }}>
                    {/* A refusal comes back as `message`; the box read only error_message and
                        showed a bare "Error:" (NEU-1129). */}
                    Error: {errorMessageDeviceRawData?.data?.message
                        || errorMessageDeviceRawData?.data?.error_message
                        || "Couldn't load this device's data."}
                </Typography>
            </Card>
        );
    }

    if (!isSuccessDeviceRawData) return <></>;

    const graphUnits =
        deviceRawData.register === "ActivePower"
            ? "W"
            : deviceRawData.register === "TotalActiveEnergy"
            ? "kWh"
            : "???";

    const valueData = (deviceRawData?.values || []).map((p) => [
        new Date(p.x).getTime(),
        p.y,
    ]);

    const brokerData = (deviceRawData?.broker_on_off_data || []).map((p) => [
        new Date(p.x).getTime(),
        p.y,
    ]);

    const option = {
        backgroundColor: darkBg,
        animation: false,
        grid: { top: 48, right: 24, bottom: 76, left: 72 },
        dataZoom: [
          { type: "inside", xAxisIndex: 0, filterMode: "filter" },
          {
            type: "slider",
            xAxisIndex: 0,
            height: 18,
            bottom: 32,
            backgroundColor: "rgba(255,255,255,0.03)",
            borderColor: "rgba(255,255,255,0.07)",
            fillerColor: "rgba(253,185,64,0.2)",
            handleStyle: { color: "#FDB940", borderColor: "#FDB940" },
            moveHandleStyle: { color: "#FDB940" },
            emphasis: { handleStyle: { color: "#FDB940" } },
            textStyle: { color: "transparent" },
            brushSelect: false,
            showDetail: false,
          },
        ],
        title: {
            text: `${deviceInfo?.serial_number} ${deviceRawData?.register}`,
            textStyle: { color: labelColor, fontSize: 14, fontWeight: "bold" },
            left: 12,
            top: 8,
        },
        legend: {
            top: 4,
            right: 12,
            textStyle: { color: labelColor, fontSize: 11 },
            icon: "circle",
            itemWidth: 10,
            itemHeight: 10,
        },
        tooltip: {
            trigger: "axis",
            ...tooltipStyle,
            formatter: (params) => {
                const d = new Date(params[0].value[0]);
                const dateStr = `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")} ${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}:${d.getSeconds().toString().padStart(2, "0")}`;
                return [
                    dateStr,
                    ...params.map((p) => {
                        const dot = `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${p.color};margin-right:6px"></span>`;
                        if (p.seriesName === "Broker-Online-Offline") {
                            const label = p.value[1] > 0 ? "Offline" : "Online";
                            return `${dot}${p.seriesName}: <b>${label}</b>`;
                        }
                        return `${dot}${p.seriesName}: <b>${p.value[1]}</b>`;
                    }),
                ].join("<br/>");
            },
        },
        xAxis: {
            type: "time",
            name: "Date Time",
            nameLocation: "middle",
            nameGap: 30,
            nameTextStyle: { color: labelColor, fontSize: 11 },
            axisLine: { lineStyle: { color: gridColor } },
            axisTick: { lineStyle: { color: gridColor } },
            axisLabel: {
                color: labelColor,
                fontSize: 10,
                formatter: (v) => {
                    const d = new Date(v);
                    return `${(d.getMonth() + 1)}/${d.getDate()} ${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
                },
            },
            splitLine: { show: false },
        },
        yAxis: {
            type: "value",
            name: graphUnits,
            nameTextStyle: { color: labelColor, fontSize: 11 },
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: { color: labelColor, fontSize: 10 },
            splitLine: { lineStyle: { color: gridColor, type: "dashed" } },
        },
        series: [
            {
                name: "Value",
                type: "line",
                smooth: true,
                symbol: "none",
                lineStyle: { color: colorValue, width: 1.5 },
                itemStyle: { color: colorValue },
                data: valueData,
            },
            ...(brokerData.length > 0 ? [{
                name: "Broker-Online-Offline",
                type: "line",
                step: "end",
                smooth: false,
                symbol: "none",
                lineStyle: { color: "#EF4444", width: 1 },
                itemStyle: { color: "#EF4444" },
                areaStyle: { color: "rgba(160,160,160,0.25)" },
                data: brokerData,
            }] : []),
        ],
    };

    const sn = deviceInfo?.serial_number ?? "meter";
    const reg = deviceRawData?.register ?? "data";
    const dateRange = startDate && endDate
        ? `${startDate.format("YYYY-MM-DD")}_to_${endDate.format("YYYY-MM-DD")}`
        : dayjs().format("YYYY-MM-DD");
    const filename = `${sn}-${reg}-${dateRange}`;

    const handleDownloadCsv = () => {
        if (!deviceRawData?.values?.length) return;
        let csv = `Timestamp (SAST),${reg}\r\n`;
        deviceRawData.values.forEach((pt) => {
            const val = pt.y != null ? Number(pt.y).toFixed(4) : "";
            csv += `${formatSAST(pt.x)},${val}\r\n`;
        });
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${filename}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleDownloadPng = () => {
        const url = chartRef.current?.getEchartsInstance()?.getDataURL({ type: "png", pixelRatio: 2, backgroundColor: darkBg });
        if (!url) return;
        const a = document.createElement("a");
        a.href = url;
        a.download = `${filename}.png`;
        a.click();
    };

    return (
        <Card variant="outlined" sx={{ mt: 2, backgroundColor: darkBg }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 1, pr: 1 }}>
                <GraphDownloadButton onDownloadCsv={handleDownloadCsv} onDownloadPng={handleDownloadPng} />
            </Box>
            <Box sx={{ p: 1 }}>
                <ReactECharts ref={chartRef} option={option} style={{ height: 450 }} notMerge />
            </Box>
        </Card>
    );
}
